import { useCallback, useEffect, useId, useRef } from 'react';
import { announceToScreenReader } from '../utils/a11y.utils';

const useListboxDropdown = ({
	isOpen,
	setIsOpen,
	optionCount,
	onSelectIndex,
	label,
}) => {
	const listboxId = useId();
	const toggleId = useId();
	const optionRefs = useRef([]);

	const focusOption = useCallback((index) => {
		const option = optionRefs.current[index];
		if (option) option.focus();
	}, []);

	const close = useCallback(() => {
		setIsOpen(false);
		const toggle = document.getElementById(toggleId);
		if (toggle) toggle.focus();
		if (label) announceToScreenReader(`${label} collapsed`);
	}, [setIsOpen, toggleId, label]);

	const open = useCallback(() => {
		setIsOpen(true);
		if (label) announceToScreenReader(`${label} expanded`);
	}, [setIsOpen, label]);

	useEffect(() => {
		if (!isOpen || optionCount === 0) return undefined;
		const rafId = requestAnimationFrame(() => focusOption(0));
		return () => cancelAnimationFrame(rafId);
	}, [isOpen, optionCount, focusOption]);

	const handleToggleKeyDown = useCallback(
		(event) => {
			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				event.preventDefault();
				if (!isOpen) {
					open();
				} else {
					focusOption(event.key === 'ArrowDown' ? 0 : optionCount - 1);
				}
			}
		},
		[isOpen, open, focusOption, optionCount]
	);

	const handleOptionKeyDown = useCallback(
		(event, index) => {
			switch (event.key) {
				case 'ArrowDown':
					event.preventDefault();
					focusOption(Math.min(index + 1, optionCount - 1));
					break;
				case 'ArrowUp':
					event.preventDefault();
					focusOption(Math.max(index - 1, 0));
					break;
				case 'Home':
					event.preventDefault();
					focusOption(0);
					break;
				case 'End':
					event.preventDefault();
					focusOption(optionCount - 1);
					break;
				case 'Escape':
					event.preventDefault();
					close();
					break;
				case 'Enter':
				case ' ':
					event.preventDefault();
					onSelectIndex(index);
					break;
				default:
					break;
			}
		},
		[close, focusOption, onSelectIndex, optionCount]
	);

	const setOptionRef = useCallback((index, node) => {
		optionRefs.current[index] = node;
	}, []);

	return {
		listboxId,
		toggleId,
		close,
		open,
		handleToggleKeyDown,
		handleOptionKeyDown,
		setOptionRef,
	};
};

export default useListboxDropdown;
